/**
 * The reviewed import, cut into the requests the server takes (`D201`).
 *
 * ── WHAT IS LEFT OUT, AND WHY IT IS LEFT OUT HERE ──────────────────────────
 * A set of a name the person skipped (or never matched) is not sent; a set
 * with no reps, seconds or metres is not a set this app can log (`D151`) and
 * is not sent; a workout left with no sets is not sent; a workout that starts
 * in the future is a wrong clock and is not sent; a workout with more sets
 * than a session may hold keeps the first {@link MAX_SETS_PER_WORKOUT}. Each
 * is counted, so the screen can say what was skipped instead of letting the
 * server refuse a batch for it.
 *
 * ── BATCHES ────────────────────────────────────────────────────────────────
 * At most {@link MAX_IMPORT_SESSIONS} workouts and {@link MAX_IMPORT_SETS}
 * sets per request — the contract's bounds — in date order, so a stopped
 * import has imported the oldest history first and a retry resends the rest.
 */
import {
  type ImportBatchInput,
  MAX_IMPORT_SESSIONS,
  MAX_IMPORT_SETS,
} from "@repo/contract/training";
import type { SourceApp, SourceWorkout } from "./sources";

/** `WorkoutSession`'s own ceiling (`MAX_SETS_PER_SESSION`). */
export const MAX_SETS_PER_WORKOUT = 500;

export interface ImportPlan {
  readonly batches: readonly ImportBatchInput[];
  readonly sets: number;
  readonly skippedSets: number;
  readonly skippedWorkouts: number;
  readonly workouts: number;
}

type Session = ImportBatchInput["sessions"][number];

export function buildImport(params: {
  /** Source name → catalogue id, or `null` for "não importar". */
  readonly choices: ReadonlyMap<string, string | null>;
  readonly now: Date;
  readonly source: SourceApp;
  readonly workouts: readonly SourceWorkout[];
}): ImportPlan {
  const sessions: Session[] = [];
  let skippedSets = 0;
  let skippedWorkouts = 0;
  for (const workout of params.workouts) {
    if (workout.startedAt.getTime() > params.now.getTime()) {
      skippedWorkouts += 1;
      skippedSets += workout.sets.length;
      continue;
    }
    const sets: Session["sets"][number][] = [];
    for (const set of workout.sets) {
      const exerciseId = params.choices.get(set.exerciseName) ?? null;
      const measured =
        set.reps !== null ||
        set.durationSeconds !== null ||
        set.distanceMetres !== null;
      if (
        exerciseId === null ||
        !measured ||
        sets.length >= MAX_SETS_PER_WORKOUT
      ) {
        skippedSets += 1;
        continue;
      }
      sets.push({
        distanceMetres: set.distanceMetres,
        durationSeconds: set.durationSeconds,
        exerciseId,
        kind: set.kind,
        load: set.loadKg,
        reps: set.reps,
        rpe: set.rpe,
      });
    }
    if (sets.length === 0) {
      skippedWorkouts += 1;
      continue;
    }
    const endedAt =
      workout.endedAt.getTime() > params.now.getTime()
        ? params.now
        : workout.endedAt;
    sessions.push({
      endedAt: endedAt.toISOString(),
      importId: workout.importId,
      sets,
      startedAt: workout.startedAt.toISOString(),
    });
  }
  return {
    batches: toBatches(sessions, params.source),
    sets: sessions.reduce((sum, session) => sum + session.sets.length, 0),
    skippedSets,
    skippedWorkouts,
    workouts: sessions.length,
  };
}

function toBatches(
  sessions: readonly Session[],
  source: SourceApp
): ImportBatchInput[] {
  const batches: ImportBatchInput[] = [];
  let current: Session[] = [];
  let sets = 0;
  for (const session of sessions) {
    const full =
      current.length >= MAX_IMPORT_SESSIONS ||
      sets + session.sets.length > MAX_IMPORT_SETS;
    if (full && current.length > 0) {
      batches.push({ sessions: current, source });
      current = [];
      sets = 0;
    }
    current.push(session);
    sets += session.sets.length;
  }
  if (current.length > 0) {
    batches.push({ sessions: current, source });
  }
  return batches;
}

/** Every distinct exercise name in the file, most-used first — the order the
 *  review shows them in, so the names that matter most are checked first. */
export function distinctNames(workouts: readonly SourceWorkout[]): string[] {
  const counts = new Map<string, number>();
  for (const workout of workouts) {
    for (const set of workout.sets) {
      counts.set(set.exerciseName, (counts.get(set.exerciseName) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name]) => name);
}
